import { logger } from '../../utils/logger';
import { api } from './base';

export const conversationsApi = {
  list: async (includeArchived = false, nextToken?: string) => {
    logger.debug('[conversationsApi] Calling GET /conversations with includeArchived:', includeArchived, 'nextToken:', nextToken?.substring(0, 20));
    const params: Record<string, string> = { includeArchived: includeArchived.toString() };
    if (nextToken) {
      params.nextToken = nextToken;
    }
    
    const response = await api.get('/conversations', { params });
    logger.debug('[conversationsApi] Response status:', response.status);
    logger.debug('[conversationsApi] response.data.data length:', response.data.data?.length);
    logger.debug('[conversationsApi] Has nextToken:', !!response.data.nextToken);
    
    return {
      conversations: response.data.data,
      nextToken: response.data.nextToken,
    };
  },

  get: async (conversationId: string) => {
    const response = await api.get(`/conversations/${conversationId}`);
    return response.data.data;
  },

  // Paginated message loading for scroll-to-load older messages
  getOlderMessages: async (conversationId: string, beforeTimestamp?: string, limit = 20) => {
    const response = await api.get(`/conversations/${conversationId}/messages`, {
      params: { 
        before: beforeTimestamp,
        limit: limit.toString()
      }
    });
    return response.data.data as {
      messages: Array<{ role: string; content: string; timestamp: string; id?: string }>;
      hasMore: boolean;
      totalCount: number;
    };
  },

  create: async (data: { 
    title?: string; 
    settings?: { model?: string; mode?: string; modelOverrides?: Record<string, string> }; 
    context?: Record<string, unknown>; 
    initialMessage?: string;
    scope?: 'project' | 'global';
    projectId?: string;
  }) => {
    const response = await api.post('/conversations', data);
    return response.data.data;
  },

  update: async (conversationId: string, updates: Record<string, unknown>) => {
    const response = await api.patch(`/conversations/${conversationId}`, updates);
    return response.data.data;
  },

  delete: async (conversationId: string, hard = false) => {
    const response = await api.delete(`/conversations/${conversationId}`, {
      params: { hard: hard.toString() }
    });
    return response.data.data;
  },

  generateTitle: async (conversationId: string) => {
    const response = await api.post(`/conversations/${conversationId}/generate-title`);
    return response.data.data;
  },
  
  // Link-based architecture: artifact linking
  linkArtifact: async (conversationId: string, link: { artifactType: string; artifactId: string; artifactName: string; projectId: string }) => {
    const response = await api.post(`/conversations/${conversationId}/link`, link);
    return response.data.data;
  },
  
  unlinkArtifact: async (conversationId: string, artifactId: string) => {
    // Backend uses POST /unlink with artifactId in body
    const response = await api.post(`/conversations/${conversationId}/unlink`, { artifactId });
    return response.data.data;
  },
  
  unlinkAll: async (conversationId: string) => {
    // Backend uses POST /unlink with unlinkAll flag
    const response = await api.post(`/conversations/${conversationId}/unlink`, { unlinkAll: true });
    return response.data.data;
  },
  
  // Get conversations linked to a specific artifact
  getByArtifact: async (artifactType: string, artifactId: string) => {
    const response = await api.get('/conversations/by-artifact', {
      params: { artifactType, artifactId }
    });
    return response.data.data;
  },
};
